import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Users, Send, Building2, FileText, FormInput, Mail, TrendingUp, Inbox } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatCard = ({ icon: Icon, label, value, color, delay }) => (
  <div
    className={`bg-[#121212] border border-[rgba(255,255,255,0.12)] rounded-lg p-5 animate-fade-in stagger-${delay}`}
    data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}
  >
    <div className="flex items-center justify-between mb-3">
      <Icon size={20} strokeWidth={1.5} style={{ color }} />
      <span className="text-2xl font-bold text-white" style={{ fontFamily: 'Manrope' }}>{value}</span>
    </div>
    <p className="text-sm text-[#B0B0B0]">{label}</p>
  </div>
);

export default function DashboardPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await axios.get(`${API}/dashboard/stats`, { withCredentials: true });
        setStats(res.data);
      } catch (e) {
        console.error('Failed to load stats', e);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  if (loading) {
    return (
      <div className="flex-1 p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-[#1A1A1A] rounded w-48" />
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[1,2,3,4].map(i => <div key={i} className="h-24 bg-[#1A1A1A] rounded-lg" />)}
          </div>
        </div>
      </div>
    );
  }

  const statCards = [
    { icon: Users, label: 'Total Contacts', value: stats?.total_contacts || 0, color: '#00FF99' },
    { icon: Send, label: 'Campaigns', value: stats?.total_campaigns || 0, color: '#007AFF' },
    { icon: Building2, label: 'Businesses', value: stats?.total_businesses || 0, color: '#FFB800' },
    { icon: Mail, label: 'Mailing Lists', value: stats?.total_lists || 0, color: '#FF3B30' },
    { icon: FileText, label: 'Templates', value: stats?.total_templates || 0, color: '#B0B0B0' },
    { icon: FormInput, label: 'Lead Forms', value: stats?.total_forms || 0, color: '#00FF99' },
    { icon: TrendingUp, label: 'Emails Sent', value: stats?.sent_campaigns || 0, color: '#007AFF' },
    { icon: Inbox, label: 'Total Leads', value: stats?.total_leads || 0, color: '#FFB800' },
  ];

  return (
    <div className="flex-1 p-8" data-testid="dashboard-page">
      <div className="mb-8">
        <h1 className="text-3xl font-bold tracking-tight text-white" style={{ fontFamily: 'Manrope' }}>
          Welcome back, {user?.name?.split(' ')[0] || 'User'}
        </h1>
        <p className="text-[#B0B0B0] mt-1">Here's your CRM overview</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {statCards.map((card, i) => (
          <StatCard key={card.label} {...card} delay={(i % 4) + 1} />
        ))}
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Contacts */}
        <div className="bg-[#121212] border border-[rgba(255,255,255,0.12)] rounded-lg p-5">
          <h2 className="text-lg font-bold text-white mb-4" style={{ fontFamily: 'Manrope' }}>
            Recent Contacts
          </h2>
          {stats?.recent_contacts?.length > 0 ? (
            <div className="space-y-3">
              {stats.recent_contacts.map(c => (
                <div key={c.contact_id} className="flex items-center justify-between py-2 border-b border-[rgba(255,255,255,0.06)] last:border-0">
                  <div>
                    <p className="text-sm text-white">{c.name}</p>
                    <p className="text-xs text-[#808080]">{c.email || c.phone || 'No contact info'}</p>
                  </div>
                  <span className="text-xs text-[#808080]">
                    {c.business_id ? 'Assigned' : 'Unassigned'}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-[#808080]">No contacts yet</p>
          )}
        </div>

        {/* Recent Campaigns */}
        <div className="bg-[#121212] border border-[rgba(255,255,255,0.12)] rounded-lg p-5">
          <h2 className="text-lg font-bold text-white mb-4" style={{ fontFamily: 'Manrope' }}>
            Recent Campaigns
          </h2>
          {stats?.recent_campaigns?.length > 0 ? (
            <div className="space-y-3">
              {stats.recent_campaigns.map(c => (
                <div key={c.campaign_id} className="flex items-center justify-between py-2 border-b border-[rgba(255,255,255,0.06)] last:border-0">
                  <div>
                    <p className="text-sm text-white">{c.name}</p>
                    <p className="text-xs text-[#808080]">{c.subject}</p>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded ${
                    c.status === 'sent' ? 'bg-[#00FF99]/10 text-[#00FF99]' :
                    c.status === 'draft' ? 'bg-[#FFB800]/10 text-[#FFB800]' :
                    'bg-[#808080]/10 text-[#808080]'
                  }`}>
                    {c.status}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-[#808080]">No campaigns yet</p>
          )}
        </div>
      </div>
    </div>
  );
}
